import { useEffect, useCallback, useRef } from 'react';
import { useIncidentStore } from '@/store/incident.store';
import { useMessagingStore } from '@/store/messaging.store';
import { useConversations } from './useMessages';

/**
 * Hook that automatically joins incident threads when an incident is activated.
 *
 * When a user activates an incident:
 * 1. Finds the corresponding incident conversation
 * 2. Opens the messaging panel
 * 3. Sets the incident conversation as active
 *
 * This ensures users are immediately connected to their incident's
 * communication thread for real-time collaboration.
 */
export function useIncidentThreadAutoJoin() {
  const { activeIncident } = useIncidentStore();
  const {
    conversations,
    setActiveConversation,
    setPanelOpen,
  } = useMessagingStore();
  const { data: conversationsData } = useConversations();

  // Find the incident conversation
  const findIncidentConversation = useCallback(
    (incidentId: string) => {
      // Check store conversations first
      const storeConversation = conversations.find(
        (c) => c.type === 'incident' && c.incident_id === incidentId
      );
      if (storeConversation) return storeConversation;

      // Check fetched conversations
      const fetchedConversation = conversationsData?.data?.find(
        (c) => c.type === 'incident' && c.incident_id === incidentId
      );
      return fetchedConversation;
    },
    [conversations, conversationsData?.data]
  );

  // Track which incident we last auto-opened for, so we only open once per switch
  const lastAutoOpenedIncidentId = useRef<string | null>(null);

  // Auto-join when incident is activated (only once per incident switch)
  useEffect(() => {
    if (!activeIncident?.id) {
      lastAutoOpenedIncidentId.current = null;
      return;
    }

    // Skip if we already auto-opened for this incident
    if (lastAutoOpenedIncidentId.current === activeIncident.id) return;

    const conversation = findIncidentConversation(activeIncident.id);

    if (conversation) {
      lastAutoOpenedIncidentId.current = activeIncident.id;
      setActiveConversation(conversation.id);
      // Join in background without opening panel
    }
  }, [activeIncident?.id, findIncidentConversation, setActiveConversation, setPanelOpen]);

  // Manual function to join incident thread
  const joinIncidentThread = useCallback(
    (incidentId: string) => {
      const conversation = findIncidentConversation(incidentId);
      if (conversation) {
        setActiveConversation(conversation.id);
        setPanelOpen(true);
        return true;
      }
      return false;
    },
    [findIncidentConversation, setActiveConversation, setPanelOpen]
  );

  return {
    joinIncidentThread,
    hasIncidentThread: activeIncident?.id
      ? !!findIncidentConversation(activeIncident.id)
      : false,
  };
}
